import { Stack } from '@mantine/core';
import type {
	ImportConfig,
	ImportJobIn,
	ImportResultOut,
} from '@repo/api-codegen';
import {
	useApiCreateMonitorsFromJob,
	useApiGetJobResult,
	useApiStartJob,
} from '@repo/api-codegen';
import { useCallback, useState } from 'react';
import { useIntegration } from '../../api/hooks/integration';
import { FooterButtons } from './components/FooterButtons';
import { ImportSelect } from './components/ImportSelect';
import { SourceConfiguration } from './components/SourceConfiguration';
import { ImportStep } from './types';

interface MetaplaneImportControllerProps {
	onComplete: (successMessage: string) => void;
	onCancel: () => void;
}

export function MetaplaneImportController({
	onComplete,
	onCancel,
}: MetaplaneImportControllerProps) {
	// Core state - only what we can't derive
	const [jobId, setJobId] = useState<string | null>(null);
	const [selectedMonitors, setSelectedMonitors] = useState<string[]>([]);
	const [importStatus, setImportStatus] = useState<
		'idle' | 'importing' | 'success' | 'error'
	>('idle');
	const [importError, setImportError] = useState<string | null>(null);

	// Form state for credentials step
	const [apiKey, setApiKey] = useState('');
	const [sourceId, setSourceId] = useState('');
	const [integrationId, setIntegrationId] = useState('');

	// API hooks
	const startJobMutation = useApiStartJob();
	const createMonitorsMutation = useApiCreateMonitorsFromJob();

	const { data: jobResult, isLoading: isJobLoading } = useApiGetJobResult(
		{ pathParams: { jobId: jobId || '' } },
		{
			enabled: !!jobId,
			refetchInterval: (query) => {
				const { data } = query.state;
				if (!data) return 2000;

				const result = data as ImportResultOut;

				// Auto-select all monitors when they become available (only if there are monitors)
				if (
					result.status === 'ready' &&
					result.results.length > 0 &&
					selectedMonitors.length === 0
				) {
					setSelectedMonitors(result.results.map((m) => m.id));
				}

				if (result.status === 'ready' || result.status === 'error') {
					return false;
				}
				return 2000;
			},
		}
	);

	// Fetch integration information
	const { data: integration } = useIntegration({
		id: integrationId,
		options: {
			enabled: !!integrationId,
		},
	});

	// Derive state from React Query results
	const result = jobResult as ImportResultOut | undefined;
	const currentStep =
		result?.status === 'ready' && result.results.length > 0
			? ImportStep.MONITOR_SELECTION
			: ImportStep.CREDENTIALS;
	const availableMonitors = result?.status === 'ready' ? result.results : [];

	// Determine error message based on result status
	let errorMessage: string | null = null;
	if (result?.status === 'error') {
		errorMessage =
			result.error_message || 'An unknown error occurred during import.';
	} else if (result?.status === 'ready' && result.results.length === 0) {
		errorMessage = 'Could not find any monitors';
	}

	// Loading states - credentials step is loading if mutation is pending OR if we have a jobId but no final result yet
	const isCredentialsLoading =
		startJobMutation.isPending ||
		(!!jobId && result?.status !== 'ready' && result?.status !== 'error');
	const isJobResultLoading = isJobLoading && !!jobId;

	const handleValidateCredentials = useCallback(
		async (credentialsData: ImportConfig) => {
			try {
				const importJobIn: ImportJobIn = { config: credentialsData };
				const result = await startJobMutation.mutateAsync({
					body: importJobIn,
				});

				if (result.success && result.job_id) {
					setJobId(result.job_id);
				}
				// Error handling is now done through the mutation state
			} catch (error: any) {
				// Error handling is now done through the mutation state
			}
		},
		[startJobMutation]
	);

	const handleMonitorSelection = useCallback(
		(monitorIds: string[]) => {
			setSelectedMonitors(monitorIds);
			// Clear import errors when selection changes
			if (importError) {
				setImportError(null);
				setImportStatus('idle');
			}
		},
		[importError]
	);

	const handleImportMonitors = useCallback(async () => {
		if (!jobId || selectedMonitors.length === 0) {
			return;
		}

		setImportStatus('importing');
		setImportError(null);

		try {
			const importCreateIn = {
				ids: selectedMonitors,
			};

			const result = await createMonitorsMutation.mutateAsync({
				pathParams: { jobId },
				body: importCreateIn,
			});

			if (result.success) {
				setImportStatus('success');
				const successMessage = `${selectedMonitors.length} monitor${selectedMonitors.length === 1 ? '' : 's'} ${selectedMonitors.length === 1 ? 'was' : 'were'} successfully imported`;
				onComplete(successMessage);
			} else {
				setImportStatus('error');
				const errorMsg = result.error_message || 'Failed to import monitors.';
				setImportError(errorMsg);
			}
		} catch (error: any) {
			setImportStatus('error');
			setImportError(
				error.message || 'An unexpected error occurred during import.'
			);
		}
	}, [jobId, selectedMonitors, createMonitorsMutation, onComplete]);

	const handleNext = useCallback(() => {
		if (currentStep === ImportStep.CREDENTIALS) {
			// Validate credentials when Next is clicked
			if (!apiKey.trim() || !sourceId.trim() || !integrationId) {
				return;
			}

			const credentialsData: ImportConfig = {
				type: 'metaplane',
				api_key: apiKey.trim(),
				source_id: sourceId.trim(),
				integration_id: integrationId,
			};

			handleValidateCredentials(credentialsData);
		}
	}, [currentStep, apiKey, sourceId, integrationId, handleValidateCredentials]);

	const handleGoBack = useCallback(() => {
		if (currentStep === ImportStep.MONITOR_SELECTION) {
			setSelectedMonitors([]);
			setJobId(null);
			// Clear import errors when going back
			setImportError(null);
			setImportStatus('idle');
		}
	}, [currentStep]);

	const canImport = selectedMonitors.length > 0 && importStatus !== 'importing';
	const isFormValid =
		currentStep === ImportStep.CREDENTIALS
			? !!(apiKey.trim() && sourceId.trim() && integrationId)
			: true;
	const isImportLoading = importStatus === 'importing';

	return (
		<Stack spacing="md">
			{currentStep === ImportStep.CREDENTIALS && (
				<SourceConfiguration
					onValidate={handleValidateCredentials}
					isLoading={isCredentialsLoading}
					apiKey={apiKey}
					sourceId={sourceId}
					integrationId={integrationId}
					onApiKeyChange={setApiKey}
					onSourceIdChange={setSourceId}
					onIntegrationChange={setIntegrationId}
					errorMessage={
						errorMessage || (startJobMutation.error as any)?.message
					}
				/>
			)}

			{currentStep === ImportStep.MONITOR_SELECTION && (
				<ImportSelect
					monitors={availableMonitors}
					selectedMonitors={selectedMonitors}
					onSelectionChange={handleMonitorSelection}
					integrationType={integration?.type}
					importError={importError}
				/>
			)}

			<FooterButtons
				currentStep={currentStep}
				isLoading={
					isCredentialsLoading || isJobResultLoading || isImportLoading
				}
				canImport={canImport}
				canNext={isFormValid}
				onNext={handleNext}
				onBack={handleGoBack}
				onCancel={onCancel}
				onImport={handleImportMonitors}
				showRetrievingData={isJobResultLoading}
			/>
		</Stack>
	);
}
